import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { DropdownOptions } from 'app/core/components/dropdown-selection/dropdown-selection.component';
import { SchoolActivity, SchoolActivityInstance } from 'app/models';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import {
	CUSTOM_SCHEDULE_DATE_KEY,
	CUSTOM_SCHEDULE_REPEAT_KEY,
	CUSTOM_SCHEDULE_TIME_RANGE_KEY,
	getRepeatOptions,
	SCHEDULE_REPEAT_END_DATE_KEY,
} from '../activity-form-util';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'sp-activity-schedule-form-custom',
	template: ` <div>
		<div class="tw-flex tw-flex-col tw-gap-8 tw-pb-3" *ngIf="startDateControl | formValue | async as startDate">
			<sp-calendar-input label="Date" [control]="startDateControl" [setCallback]="startDateSelected.bind(this)"></sp-calendar-input>

			<div>
				<div class="tw-text-navy-500 tw-text-base tw-font-semibold tw-mb-3">Time</div>
				<sp-time-range-input [control]="timeRangeControl" [additionalDuration]="30"></sp-time-range-input>
			</div>

			<div class="tw-flex tw-flex-col tw-gap-3" *ngIf="repeatControl | formValue | async as repeat">
				<sp-select
					label="Repeats"
					placeholder=""
					[options]="getRepeatOptions(startDate.value)"
					[selectedOption]="getSelectedRepeatOption(activity, startDate.value)"
					(selected)="handleRepeatSelect($event)"></sp-select>
				<sp-calendar-input *ngIf="repeat.value" [control]="repeatEndControl" [setCallback]="endDateSelected.bind(this)"></sp-calendar-input>
			</div>
		</div>
	</div>`,
})
export class ActivityScheduleCustomFormComponent implements OnInit {
	@Input() editScheduleForm!: UntypedFormGroup;
	@Input() activity!: SchoolActivity | null;
	@Input() activityInstances!: BehaviorSubject<SchoolActivityInstance[]> | null;
	@Input() showCalendar!: (field: ElementRef, control: UntypedFormControl, callback: ($event: moment.Moment[]) => void) => void;

	startDateControl!: UntypedFormControl;
	timeRangeControl!: UntypedFormControl;
	repeatControl!: UntypedFormControl;
	repeatEndControl!: UntypedFormControl;

	constructor() {}

	ngOnInit(): void {
		this.startDateControl = this.editScheduleForm.get(CUSTOM_SCHEDULE_DATE_KEY) as UntypedFormControl;
		this.timeRangeControl = this.editScheduleForm.get(CUSTOM_SCHEDULE_TIME_RANGE_KEY) as UntypedFormControl;
		this.repeatControl = this.editScheduleForm.get(CUSTOM_SCHEDULE_REPEAT_KEY) as UntypedFormControl;
		this.repeatEndControl = this.editScheduleForm.get(SCHEDULE_REPEAT_END_DATE_KEY) as UntypedFormControl;
	}

	getRepeatOptions(date: moment.Moment): DropdownOptions<string>[] {
		return getRepeatOptions(date);
	}

	getSelectedRepeatOption(activity: SchoolActivity | null, date: moment.Moment): DropdownOptions<string> | null {
		const options = getRepeatOptions(date);
		const selected = this.editScheduleForm.value[CUSTOM_SCHEDULE_REPEAT_KEY] ?? activity?.custom_repeat_rule;
		return options.find((opt) => opt.value === selected) ?? options[0];
	}

	handleRepeatSelect(selected: DropdownOptions<string>): void {
		this.editScheduleForm.patchValue({ [CUSTOM_SCHEDULE_REPEAT_KEY]: selected.value });
	}

	startDateSelected($event: moment.Moment[]): void {
		const startDate = $event[0].startOf('day');
		this.editScheduleForm.patchValue({
			[CUSTOM_SCHEDULE_DATE_KEY]: startDate,
			[CUSTOM_SCHEDULE_REPEAT_KEY]: '',
			[SCHEDULE_REPEAT_END_DATE_KEY]: moment(startDate).add(4, 'months'),
		});
	}

	endDateSelected($event: moment.Moment[]): void {
		const endDate = $event[0].endOf('day');
		this.editScheduleForm.patchValue({ [SCHEDULE_REPEAT_END_DATE_KEY]: endDate });
	}
}
